import { makeAutoObservable, toJS } from 'mobx'
import { cartMobx, restaurantConfig, productMobx, restaurantCenter, RESTAURANT_EVENT, Console, storeMobx } from '..'
import allMenuMobx from './allMenu.mobx'
import { toDecimal, pk } from '@dt/fe-utils'
import { mapValues, groupBy, sumBy, values, pick, merge, flatten, keyBy } from '@dt/fe-utils/dist/lodash'
import FeeAPI from 'api/fee.api'

const console = new Console('console.fee')

/**
 * 套餐价格分摊
 * 套餐内餐品总价 = sum(餐品数量 * 餐品价)
 */
const apportionCombo = (item: IViewCartItem | IViewOrderDetail, viewComboGroups: any) => {
  // 购物车餐品数据
  const { productGroups, totalProductPrice, realPrice, modifierPrice, cnt } = item
  if (!productGroups?.length) return

  // 菜单中套餐数据
  const productsInViewCombo: any[] = flatten(viewComboGroups?.map((group: any) => group.productGroupDetails) as any[])
  const productsInViewComboMap = keyBy(productsInViewCombo, (item) => {
    return `${item.groupNo}_${item.productNo}_${item.scaleNo}`
  })

  // 定义
  const products: any[] = flatten(productGroups?.map(group => group.productGroupDetails) as any[])
  const totalAddPrice = products.reduce((p: number, item: any) => {
    return (item.addPrice || 0) + p
  }, 0)
  const totalPrice = products.reduce((p: number, item: any) => {
    return (((productsInViewComboMap?.[`${item.groupNo}_${item.productNo}_${item.scaleNo}`] || item)?.productPrice || 0) * (item.cnt || 1)) + p
  }, 0)
  const items: any[] = []

  // 计算分摊
  products?.forEach(item => {
    const _productPrice = ((productsInViewComboMap?.[`${item.groupNo}_${item.productNo}_${item.scaleNo}`] || item)?.productPrice || 0) * (item.cnt || 1) * cnt
    items.push({
      pk: item.pk,
      totalPrice: toDecimal(_productPrice / totalPrice * (totalProductPrice - totalAddPrice) + (item.modifierPrice || 0) + (item.addPrice || 0)),
      realPrice: toDecimal(_productPrice / totalPrice * ((realPrice - totalAddPrice) / cnt - modifierPrice)) + (item.modifierPrice || 0) + (item.addPrice || 0),
      cnt: (item.cnt || 1) * cnt,
    })
  })

  // 处理溢出和不足
  const sumTotalPrice = items.reduce((p: number, item: any) => (item.totalPrice || 0) + p, 0)
  const sumRealPrice = items.reduce((p: number, item: any) => (item.realPrice || 0) + p, 0)
  if (items?.[items.length - 1]) {
    items[items.length - 1].totalPrice += toDecimal(totalProductPrice + modifierPrice - sumTotalPrice)
    items[items.length - 1].realPrice += toDecimal(realPrice - sumRealPrice)
  }
  return keyBy(items, 'pk')
}

class Fee {
  constructor () {
    makeAutoObservable(this, {
      taxMapConfig: false
    })
    // 费用的变化更新事件
    restaurantCenter.on(RESTAURANT_EVENT.FEE_CONFIG_UPDATED, () => {
      this.calcFee()
    })
    restaurantCenter.on(RESTAURANT_EVENT.MENU_END, () => {
      this.calcFee()
    })
  }

  /** 税的配置树，当前跟sdk一致，需要统一typing */
  taxMapConfig: ITaxMapConfig = {}

  /** 服务费配置 */
  gratuityConfig: IGratuityConfig = {
    name: ''
  }

  /** 税 */
  calculatedTax: ICalculatedTax[] = []

  /** 税总 */
  calculatedTaxTotal = 0

  /** 免税总 */
  calculatedFreeTaxTotal = 0

  /** 含税总 */

  calculatedShowTaxTotal = 0

  /** 服务费 */
  calculatedServiceCharge: ICalculatedServiceCharge[] = []

  /** 服务费总 */
  calculatedServiceChargeTotal = 0

  /** 是否免税的action */
  taxFreeAction: { [pk: string]: boolean } = {}

  /** pk 不需要含税的金额 */
  get pkToTax (): { [pk: string]: number } {
    return mapValues(groupBy(this.calculatedTax.filter(taxItem => !taxItem.taxInPrice), 'pk'), (taxItems) => sumBy(taxItems, 'amount'))
  }

  /** pk 不需要含税的金额 */
  get pkToFreeTax (): { [pk: string]: number } {
    return this.pkToTax
  }

  /** pk 含税价的金额map */
  get pkToShowTax (): { [pk: string]: number } {
    return mapValues(groupBy(this.calculatedTax.filter(taxItem => taxItem.taxInPrice), 'pk'), (taxItems) => sumBy(taxItems, 'amount'))
  }

  /** 就餐方式 */
  get diningOption () {
    return ['pickup', 'carside'].includes(cartMobx.info.eatType) ? 'togo' : cartMobx.info.eatType
  }

  /** 税分类 */
  get taxCategoryToTax () {
    const cityId = cartMobx.info.cityId || storeMobx.info.cityId
    const eatType = this.diningOption
    if (cityId && this.taxMapConfig[cityId]) {
      if (eatType && this.taxMapConfig[cityId][eatType]) {
        return this.taxMapConfig[cityId][eatType]
      }
    }
    return {}
  }

  /** 税分类结果 */
  get taxCategoryResult () {
    return values(groupBy(this.calculatedTax, 'taxCategoryId')).map(items => {
      const taxCategoryId = items[0].taxCategoryId
      const rate = items[0].rate
      const name = items[0].taxName
      return {
        taxCategoryId,
        amount: sumBy(items, item => item.isTaxFree ? 0 : item.amount),
        name,
        rate,
        // isTaxFree: cartMobx.info.freeTaxList.includes(taxCategoryId),
        items
      }
    })
  }

  /** 置入税和服务费 */
  setFee (data: any) {
    if (productMobx.channel !== '3rdDelivery') {
      this.calculatedServiceCharge = data.gratuities
      this.calculatedServiceChargeTotal = data.gratuityTotal
      this.calculatedTax = data.taxes.concat(data.gratuityTaxes || [], data.expenseTaxes || [])
      this.calculatedFreeTaxTotal = toDecimal(data.freeTaxTotal)
      this.calculatedTaxTotal = toDecimal(data.taxOutPrice)
      this.calculatedShowTaxTotal = toDecimal(data.showTaxTotal)
    } else {
      this.calculatedServiceCharge = []
      this.calculatedServiceChargeTotal = 0
      this.calculatedTax = data.taxes.concat(data.expenseTaxes || [])
      this.calculatedFreeTaxTotal = toDecimal(data.freeTaxTotal)
      this.calculatedTaxTotal = toDecimal(data.taxOutPrice)
      this.calculatedShowTaxTotal = toDecimal(data.showTaxTotal)
    }
  }

  /** 获取计算数据 */
  getCalcData () {
    const cityId = cartMobx.info.cityId || storeMobx.info.cityId
    const diningOption = this.diningOption as TCalcTaxAndServiceDiningOption
    const { expressPrice = 0, discountExpressPrice = 0, boxPrice, discountBoxPrice = 0 } = cartMobx.cartResult
    const expressTaxCategoryId = storeMobx.info.selfDeliveryConfig?.taxCategoryId || 0
    const boxPriceTaxCategoryId = storeMobx.info.boxTaxCategoryId || 0
    const items: any[] = []
    cartMobx.availableItems.forEach(item => {
      // 餐品
      if (item.type === 'product') {
        items.push(item)
      }
      // 换购品税费
      if (item.swapBuyProducts?.length) {
        items.push(...item.swapBuyProducts)
      }
      // 满送品税费
      if (item.fullItemNFreeProducts?.length) {
        items.push(...item.fullItemNFreeProducts)
      }
      // 套餐(固定套餐、自选套餐)
      if (['FixedCombo', 'OptionalCombo'].includes(item.type)) {
        const viewCombo = productMobx.noToViewProductMap[item.productNo]
        const pkMap = apportionCombo(item, viewCombo?.productGroups) || {}
        const comboItems: any[] = []
        item.productGroups?.forEach(group => {
          group.productGroupDetails?.forEach(detail => {
            comboItems.push({
              ...detail,
              ...pkMap[detail.pk],
              isComboChild: true
            })
          })
        })
        items.push(...comboItems)
      }
    })
    const detail = items?.map((item) => {
      const taxCategoryId = (allMenuMobx.noToViewProductMap[item.productNo] || productMobx.noToViewProductMap[item.productNo])?.taxCategoryId || (item as any).taxCategoryId || 0
      let taxInPrice = (allMenuMobx.noToViewProductMap[item.productNo] || productMobx.noToViewProductMap[item.productNo])?.taxInPrice || false
      if (!(allMenuMobx.noToViewProductMap[item.productNo] || productMobx.noToViewProductMap[item.productNo])) {
        taxInPrice = ((item as any).showTax > 0) || false
      }
      if (!taxCategoryId) {
        console.error('餐品' + item.name + '找不到税配置')
      }
      return {
        pk: item.pk,
        productNo: item.productNo,
        type: 'product',
        diningOption,
        // isTaxFree: item.isTaxFree,
        originalPrice: toDecimal(item.totalPrice),
        paidPrice: toDecimal(item.realPrice),
        taxCategoryId,
        taxInPrice,
        cnt: item.cnt,
        isComboChild: !!item.isComboChild
      }
    })
    // 其它费用 (配送费、打包费)
    const expense = []
    // 配送费
    if (diningOption === 'delivery' && cartMobx.info.express?.pk) {
      if (!expressTaxCategoryId) {
        console.error('配送费找不到税配置')
      }
      expense.push({
        pk: cartMobx.info.express?.pk || '',
        productNo: 'express',
        type: 'express',
        diningOption,
        // isTaxFree: false,
        originalPrice: toDecimal(expressPrice),
        paidPrice: toDecimal(expressPrice - discountExpressPrice),
        taxCategoryId: expressTaxCategoryId || 0,
        taxInPrice: false,
        cnt: 1
      })
    }
    // 打包费
    if (boxPrice) {
      if (!boxPriceTaxCategoryId) {
        console.error('打包费找不到税配置')
      }
      expense.push({
        pk: pk(),
        productNo: 'box',
        type: 'box',
        diningOption,
        // isTaxFree: false,
        originalPrice: toDecimal(boxPrice),
        paidPrice: toDecimal(boxPrice - discountBoxPrice),
        taxCategoryId: boxPriceTaxCategoryId || 0,
        taxInPrice: false,
        cnt: 1
      })
    }
    const calcData: ICalcTaxAndServiceChargeData = {
      cityId: cityId,
      person: cartMobx.info.person,
      diningOption,
      detail,
      expense,
      freeTaxList: toJS(cartMobx.info.freeTaxList)
    }
    return calcData
  }

  /** 计算税和服务费 */
  calcFee () {
    if (bo === 'cn') {
      return
    }
    // TODO: 2023-04-11迭代 => 三方外卖对接税
    // if (productMobx.channel === '3rdDelivery') {
    //   if (this.calculatedTaxTotal || this.calculatedShowTaxTotal || this.calculatedServiceChargeTotal) {
    //     this.clearFee()
    //   }
    // } else {
    const calcData = this.getCalcData()
    const cityId = cartMobx.info.cityId || storeMobx.info.cityId
    // 如果无餐品 则不再调用计算
    if (!(calcData?.detail.length)) {
      this.clearFee()
    } else if (!restaurantConfig.fee) {
      console.error('🍄计算税和服务费未加载')
    } else if (!cityId) {
      console.error('🍄门店city未设置，所以无法使用计算')
    } else {
      console.log('🍄计算税和服务费 input', calcData)
      // 简单的做一下认证
      if (['pos', 'kiosk'].includes(restaurantConfig.app)) {
        if (productMobx.channel === '3rdDelivery') {
          const feeResult = restaurantConfig.fee.calculate(calcData, true)
          if (feeResult && feeResult.tax?.taxes) {
            console.log('🍄计算税和服务费 output', feeResult)
            this.setFee({ ...feeResult.tax })
          } else {
            console.error('🍄计算税和服务费 error', feeResult)
          }
        } else {
          const feeResult = restaurantConfig.fee.calculate(calcData)
          if (feeResult && feeResult.tip?.gratuities && feeResult.tax?.taxes) {
            console.log('🍄计算税和服务费 output', feeResult)
            this.setFee({ ...feeResult.tax, ...feeResult.tip })
          } else {
            console.error('🍄计算税和服务费 error', feeResult)
          }
        }
      } else {
        const feeResult = restaurantConfig.fee.calculate(calcData)
        if (feeResult && feeResult.gratuities && feeResult.taxes) {
          console.log('🍄计算税和服务费 output', feeResult)
          this.setFee(feeResult)
        } else {
          console.error('🍄计算税和服务费 error', feeResult)
        }
      }
    }
    // }
  }

  /** 清空费用计算结果 */
  clearFee () {
    this.setFee({
      gratuities: [],
      gratuityTotal: 0,
      freeTaxTotal: 0,
      showTaxTotal: 0,
      taxTotal: 0,
      taxes: []
    })
  }

  /** 初始化 */
  init () {
    return FeeAPI.get().then((res: any) => {
      const { gratuity, tax, tip } = res || {}
      if (gratuity) {
        this.setGratuityConfig(gratuity)
      }
      if (tax) {
        // 转换fee配置到树形结构 cityCode -> diningOption -> productTaxCategory
        this.taxMapConfig = {}
        tax.forEach((tconfig: ITaxConfig) => {
          const cityCodeOption = tconfig.taxOptionsList.find(taxoption => taxoption.type === 'cityCode')
          const productTaxCategoryOption = tconfig.taxOptionsList.find(taxoption => taxoption.type === 'productTaxCategory')
          const diningOptionOption = tconfig.taxOptionsList.find(taxoption => taxoption.type === 'diningOption')
          const unitQuantity = tconfig.extList?.find(ext => ext.unit === 'quantity')
          const extra: ITaxItemConfigExtra = {}
          if (unitQuantity) {
            extra.quantity = {
              compare: unitQuantity.compare,
              cnt: unitQuantity.cnt,
              isGroup: unitQuantity.isGroup,
              rate: unitQuantity.rate,
            }
          }
          if (cityCodeOption && productTaxCategoryOption && diningOptionOption) {
            merge(this.taxMapConfig, {
              [cityCodeOption.targetId]: {
                [diningOptionOption.targetId.toLowerCase()]: { // 主要解决后端存储eatType的dinein使用的是dineIn的问题
                  [productTaxCategoryOption.targetId]: Object.assign(pick(tconfig, 'name', 'rate', 'roundingOption', 'category', 'id'), {
                    taxCategoryId: Number(productTaxCategoryOption.targetId),
                    extra
                  })
                }
              }
            })
          }
        })
      }
      return {
        gratuity: gratuity,
        tip: tip,
        taxConfig: this.taxMapConfig,
        cityId: storeMobx.info.cityId
      }
    })
  }

  /** 设置税 */
  setTaxFee (pk?: string) {
    if (pk) {
      this.taxFreeAction[pk] = !this.taxFreeAction[pk]
    } else {
      this.taxFreeAction['-1'] = !this.taxFreeAction['-1']
    }
  }

  /** 计算税 */
  calTaxPrice (taxCategoryId: number, price: number) {
    const theTax = this.taxCategoryToTax[taxCategoryId.toString()]
    if (theTax) {
      return restaurantConfig.fee.calTaxByProduct(theTax, price)
    } else {
      return 0
    }
  }

  /** 设置小费配置 */
  setGratuityConfig (gratuityConfig: IGratuityConfig) {
    this.gratuityConfig = {
      name: '',
      ...gratuityConfig
    }
  }
}

export default new Fee()
